import React from 'react';
import { Play, Upload, Edit, Download, Shield, Zap, Palette, Scissors, ArrowLeft } from 'lucide-react';

interface HowItWorksProps {
  onBack?: () => void;
  onStartCreating?: () => void;
}

export function HowItWorks({ onBack, onStartCreating }: HowItWorksProps) {
  const handleBack = () => {
    if (onBack) return onBack();
    window.location.href = '/';
  };

  return (
    <div className="min-h-screen bg-white text-gray-900">
      {/* Back Button */}
      <div className="container mx-auto px-4 pt-8">
        <button
          onClick={handleBack}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors mb-8"
        >
          <ArrowLeft className="w-5 h-5" />
          Back to Home
        </button>
      </div>

      {/* Hero Section */}
      <div className="container mx-auto px-4 pb-16">
        <div className="text-center mb-16">
          <h1 className="text-5xl font-bold mb-6 text-gray-900">
            How Snip Rush Works
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Create professional short-form videos in minutes with our browser-based editor. 
            No downloads, no watermarks, 100% private processing.
          </p>
        </div>

        {/* Main Process Steps */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
          <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
            <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mb-4 mx-auto">
              <Upload className="w-8 h-8" />
            </div>
            <h3 className="text-xl font-semibold mb-3 text-center">1. Upload</h3>
            <p className="text-gray-600 text-center">
              Upload your videos, images, or audio files directly in your browser. 
              Supports all major formats including MP4, MOV, PNG, JPG, and MP3.
            </p>
          </div>

          <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
            <div className="w-16 h-16 bg-purple-600 rounded-full flex items-center justify-center mb-4 mx-auto">
              <Edit className="w-8 h-8" />
            </div>
            <h3 className="text-xl font-semibold mb-3 text-center">2. Edit</h3>
            <p className="text-gray-600 text-center">
              Use our intuitive timeline editor to arrange clips, add transitions, 
              text overlays, and effects. Everything happens in your browser.
            </p>
          </div>

          <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
            <div className="w-16 h-16 bg-green-600 rounded-full flex items-center justify-center mb-4 mx-auto">
              <Zap className="w-8 h-8" />
            </div>
            <h3 className="text-xl font-semibold mb-3 text-center">3. Process</h3>
            <p className="text-gray-600 text-center">
              Our advanced browser-based processing handles everything locally. 
              No data leaves your device - complete privacy guaranteed.
            </p>
          </div>

          <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
            <div className="w-16 h-16 bg-orange-600 rounded-full flex items-center justify-center mb-4 mx-auto">
              <Download className="w-8 h-8" />
            </div>
            <h3 className="text-xl font-semibold mb-3 text-center">4. Download</h3>
            <p className="text-gray-600 text-center">
              Get your finished video instantly. Optimized for social media platforms 
              like YouTube Shorts, TikTok, and Instagram Reels.
            </p>
          </div>
        </div>

        {/* Features Section */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-center mb-12">Powerful Features</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-white rounded-lg p-6 border border-gray-200 shadow-sm">
              <Shield className="w-12 h-12 text-blue-400 mb-4" />
              <h3 className="text-xl font-semibold mb-3">100% Private</h3>
              <p className="text-gray-600">
                All processing happens in your browser. Your files never leave your device, 
                ensuring complete privacy and security.
              </p>
            </div>

            <div className="bg-white rounded-lg p-6 border border-gray-200 shadow-sm">
              <Zap className="w-12 h-12 text-yellow-400 mb-4" />
              <h3 className="text-xl font-semibold mb-3">Lightning Fast</h3>
              <p className="text-gray-600">
                Browser-based processing means no upload delays. Edit and render videos 
                instantly without waiting for cloud processing.
              </p>
            </div>

            <div className="bg-white rounded-lg p-6 border border-gray-200 shadow-sm">
              <Palette className="w-12 h-12 text-pink-400 mb-4" />
              <h3 className="text-xl font-semibold mb-3">Professional Tools</h3>
              <p className="text-gray-600">
                Advanced editing features including transitions, text overlays, 
                color correction, and custom SVG shapes.
              </p>
            </div>

            <div className="bg-white rounded-lg p-6 border border-gray-200 shadow-sm">
              <Scissors className="w-12 h-12 text-green-400 mb-4" />
              <h3 className="text-xl font-semibold mb-3">Smart Clipping</h3>
              <p className="text-gray-600">
                Intelligent video clipping tools to extract the best moments from 
                longer videos for short-form content.
              </p>
            </div>

            <div className="bg-white rounded-lg p-6 border border-gray-200 shadow-sm">
              <Play className="w-12 h-12 text-red-400 mb-4" />
              <h3 className="text-xl font-semibold mb-3">Auto Captions</h3>
              <p className="text-gray-600">
                AI-powered automatic caption generation with customizable styles 
                and timing for better engagement.
              </p>
            </div>

            <div className="bg-white rounded-lg p-6 border border-gray-200 shadow-sm">
              <Edit className="w-12 h-12 text-purple-400 mb-4" />
              <h3 className="text-xl font-semibold mb-3">No Watermarks</h3>
              <p className="text-gray-600">
                Create professional videos without any watermarks or branding. 
                Your content, your brand, your way.
              </p>
            </div>
          </div>
        </div>

        {/* Technical Details */}
        <div className="bg-white rounded-2xl p-8 border border-gray-200 shadow-sm mb-16">
          <h2 className="text-3xl font-bold text-center mb-8">Technical Excellence</h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div>
              <h3 className="text-xl font-semibold mb-4 text-blue-400">Browser Technology</h3>
              <ul className="space-y-2 text-gray-600">
                <li>• WebAssembly for high-performance video processing</li>
                <li>• WebGL for hardware-accelerated rendering</li>
                <li>• Web Workers for non-blocking operations</li>
                <li>• IndexedDB for efficient file storage</li>
                <li>• Canvas API for real-time preview</li>
              </ul>
            </div>
            <div>
              <h3 className="text-xl font-semibold mb-4 text-purple-400">Supported Formats</h3>
              <ul className="space-y-2 text-gray-600">
                <li>• Video: MP4, MOV, AVI, WebM</li>
                <li>• Audio: MP3, WAV, AAC, OGG</li>
                <li>• Images: PNG, JPG, GIF, SVG</li>
                <li>• Export: MP4 (H.264), optimized for social media</li>
                <li>• Resolutions: Up to 4K with smart downscaling</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Use Cases */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-center mb-12">Perfect For</h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-20 h-20 bg-gradient-to-r from-red-500 to-pink-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl">📱</span>
              </div>
              <h3 className="text-xl font-semibold mb-3">Social Media Creators</h3>
              <p className="text-gray-600">
                Create engaging content for TikTok, Instagram Reels, and YouTube Shorts 
                with professional editing tools.
              </p>
            </div>
            <div className="text-center">
              <div className="w-20 h-20 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl">🎓</span>
              </div>
              <h3 className="text-xl font-semibold mb-3">Educators</h3>
              <p className="text-gray-600">
                Create educational content, tutorials, and presentations with 
                easy-to-use editing tools and caption features.
              </p>
            </div>
            <div className="text-center">
              <div className="w-20 h-20 bg-gradient-to-r from-green-500 to-emerald-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl">💼</span>
              </div>
              <h3 className="text-xl font-semibold mb-3">Businesses</h3>
              <p className="text-gray-600">
                Create marketing videos, product demos, and promotional content 
                without expensive software or technical expertise.
              </p>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="text-center bg-blue-600 rounded-2xl p-12">
          <h2 className="text-3xl font-bold mb-4">Ready to Create Amazing Videos?</h2>
          <p className="text-xl text-white mb-8">
            Start editing your first video in seconds. No signup required, completely free.
          </p>
          <button 
            onClick={() => {
              if (onStartCreating) return onStartCreating();
              // Fallback to direct navigation
              window.location.href = '/';
            }}
            className="bg-white text-blue-600 px-8 py-4 rounded-lg font-semibold text-xl hover:bg-gray-100 transition-colors"
          >
            Start Creating Now
          </button>
        </div>
      </div>
    </div>
  );
}
